import { useTranslation } from 'react-i18next';

import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function todos(selector: string): Element[] {
  return [...document.querySelectorAll(selector)];
}

/** Rótulos de latón sobre las partes de `padre` que estén en pantalla. */
function etiquetasDe(
  padre: ParentNode | null,
  partes: ReadonlyArray<readonly [string, string, boolean]>,
): EtiquetaAyuda[] {
  const etiquetas: EtiquetaAyuda[] = [];
  for (const [selector, texto, debajo] of partes) {
    const el = padre?.querySelector(selector);
    if (el) {
      etiquetas.push({ el, texto, debajo });
    }
  }
  return etiquetas;
}

const CABECERA = '[data-testid="asignar-cabecera"]';
const FILA = '[data-testid="asignar-fila"]';
const HOJA_MAS = '[data-testid="auto-asignar-opciones"]';
const BARRA = '[data-testid="asignar-barra-guardar"]';

/** La primera categoría de la lista con ese estado (`gastado-de-mas`…). */
function filaConEstado(estado: string): Element[] {
  const marca =
    estado === 'falta'
      ? document.querySelector('[data-testid="asignar-falta"]')
      : document.querySelector(
          `[data-testid="asignar-estado"][data-estado="${estado}"]`,
        );
  const fila = marca?.closest(FILA);
  return fila ? [fila] : [];
}

function signo(): string | null {
  return document.querySelector(CABECERA)?.getAttribute('data-signo') ?? null;
}

function hayFilas(): boolean {
  return !!document.querySelector(FILA);
}

/** Algo en rojo: lo asignado pasa de lo que hay o una categoría se pasó. */
function hayRojo(): boolean {
  return signo() === 'negativo' || filaConEstado('gastado-de-mas').length > 0;
}

/** Una opción de la hoja «Más opciones…» (las filas de otras hojas, no). */
function opcion(id: string): Element[] {
  return uno(`${HOJA_MAS} [data-testid="accion-${id}"]`);
}

/**
 * La ayuda de «Asignar el mes» (la pantalla que abre la franja verde «listo
 * para asignar» del Plan; maqueta aprobada por Vadik el 27-oct-2026, con las
 * bromas «Pan» y «más que la mayoría de los humanos»): el recorrido entero y
 * siete preguntas. Gavi abre de verdad «Más opciones…», el teclado de una
 * categoría y la brújula, pero nunca pulsa nada que cambie el presupuesto:
 * ni asignar, ni «Hecho», ni guardar ni descartar.
 */
export function usePreguntasAsignar(): PreguntaAyuda[] {
  const { t } = useTranslation();

  const pestanas = (id: string, texto: string): PasoAyuda => ({
    id,
    escena: 'normal',
    radio: 16,
    texto,
    objetivo: () => uno('[data-testid="asignar-pestana-segmentado"]'),
  });
  const teclado = (id: string, texto: string, final = false): PasoAyuda => ({
    id,
    escena: 'asignar-teclado',
    disponible: hayFilas,
    radio: 18,
    final,
    texto,
    objetivo: () => uno('[data-testid="assign-keypad"]'),
    etiquetas: () =>
      etiquetasDe(document.querySelector('[data-testid="assign-keypad"]'), [
        ['[data-testid="keypad-done"]', t('Done'), false],
      ]),
  });
  const guardar = (id: string, texto: string, final = false): PasoAyuda => ({
    id,
    escena: 'asignar-guardar',
    radio: 14,
    final,
    texto,
    objetivo: () => uno(BARRA),
    etiquetas: () =>
      etiquetasDe(document.querySelector(BARRA), [
        ['[data-testid="asignar-descartar"]', t('Discard'), true],
        ['[data-testid="asignar-guardar"]', t('Save budget'), true],
      ]),
  });

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      radio: 22,
      texto: t(
        'Hi! Here you decide what each euro does this month. I put everything into a single category: Bread. You have more options.',
      ),
      objetivo: () => uno(CABECERA),
    },
    {
      id: 'listo',
      escena: 'normal',
      radio: 14,
      texto: t(
        'At the top, what is ready to assign: the money that has already come in and has no category yet. In green, there is still some to share out; in red, you have shared out more than you have.',
      ),
      objetivo: () => uno('[data-testid="asignar-listo"]'),
    },
    {
      id: 'faltan',
      escena: 'normal',
      radio: 18,
      texto: t(
        '"Missing · Assign" adds up what your targets ask for and what was overspent. Tap it and it breaks it down; it does not share anything out by itself.',
      ),
      objetivo: () => uno('[data-testid="asignar-infrafinanciado"]'),
    },
    {
      id: 'auto',
      escena: 'normal',
      radio: 18,
      texto: t(
        'The green button shares out in one tap what each target is missing, from top to bottom, as far as the money goes. The line above tells you first what it will do.',
      ),
      objetivo: () => uno('[data-testid="auto-asignar-bloque"]'),
      etiquetas: () =>
        etiquetasDe(
          document.querySelector('[data-testid="auto-asignar-bloque"]'),
          [
            [
              '[data-testid="auto-asignar-incluir-gastado"]',
              t('Overspending'),
              false,
            ],
            ['[data-testid="auto-asignar"]', t('Shares out in one tap'), false],
            ['[data-testid="auto-asignar-mas"]', t('More options'), true],
          ],
        ),
    },
    {
      id: 'mas',
      escena: 'asignar-mas',
      radio: 22,
      texto: t(
        '"More options…" are shortcuts so you do not have to type: repeat last month, what you spent or the three-month average. If one is grey, there is no data or the money does not stretch that far.',
      ),
      objetivo: () => uno(HOJA_MAS),
    },
    pestanas(
      'pestanas',
      t(
        'The tabs filter the list. Start with "Fixed payments": they are the ones you pay no matter what.',
      ),
    ),
    {
      id: 'fila',
      escena: 'normal',
      radio: 10,
      texto: t(
        'Each row is a category: what you give it this month and, below, how it is going. Tap it to change the amount; the "needed" pill adds what is missing in one tap.',
      ),
      objetivo: () => uno(FILA),
      etiquetas: () =>
        etiquetasDe(document.querySelector(FILA), [
          ['[data-testid="asignar-nombre"]', t('Category'), false],
          ['[data-testid="asignar-asignado"]', t('Assigned'), false],
          [
            '[data-testid="asignar-falta"], [data-testid="asignar-estado"]',
            t('How it is going'),
            true,
          ],
        ]),
    },
    {
      id: 'teclado',
      escena: 'asignar-teclado',
      disponible: hayFilas,
      radio: 18,
      texto: t(
        'Type the total for the month for that category and press "Done". The green button suggests how much.',
      ),
      objetivo: () => uno('[data-testid="assign-keypad"]'),
      etiquetas: () =>
        etiquetasDe(document.querySelector('[data-testid="assign-keypad"]'), [
          ['[data-testid^="keypad-borrador"]', t('I suggest how much'), false],
          [
            '[data-testid="keypad-cancelar"]',
            t('Close without changes'),
            false,
          ],
          ['[data-testid="keypad-done"]', t('Done'), false],
        ]),
    },
    guardar(
      'guardar',
      t(
        'What you touch here is a draft: you see how it would look, with the amount from before underneath. Nothing changes in your Plan until "Save budget". If you leave before, I will ask you.',
      ),
    ),
    {
      id: 'brujula',
      escena: 'normal',
      radio: 30,
      toque: true,
      texto: t('Now tap the compass, at the bottom right.'),
      objetivo: () => uno('[data-testid="brujula-opciones"]'),
    },
    {
      id: 'resumen',
      escena: 'brujula',
      radio: 20,
      texto: t(
        '"Month summary" tells you where what is ready to assign comes from. "Change month" takes you to share out another month.',
      ),
      objetivo: () =>
        todos('[data-testid="opcion-resumen"], [data-testid="opcion-mes"]'),
    },
    {
      id: 'final',
      escena: 'normal',
      radio: 30,
      final: true,
      texto: t(
        'Done! If you forget something, tap the porthole and I will tell you again. Your euros already know where they are going. More than most humans.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  const rojo: PasoAyuda[] = [
    {
      id: 'rojo-arriba',
      escena: 'normal',
      radio: 22,
      texto: t(
        'Red at the top: you have shared out more money than you have. You need to take it from some category until it is back to zero.',
      ),
      objetivo: () => (signo() === 'negativo' ? uno(CABECERA) : []),
    },
    {
      id: 'rojo-fila',
      escena: 'normal',
      radio: 10,
      texto: t(
        'A red row is something else: you have spent more than you gave it. If you assign what is missing, it stops being red.',
      ),
      objetivo: () => filaConEstado('gastado-de-mas'),
    },
    {
      id: 'rojo-sobra',
      escena: 'asignar-mas',
      disponible: hayRojo,
      radio: 12,
      texto: t(
        'The quick way out: "Reduce overfunding" takes what is left over from the categories with a target that have too much, and gives it back to the top.',
      ),
      objetivo: () => opcion('reduce-overfunding'),
    },
    {
      ...teclado(
        'rojo-teclado',
        t(
          'Or lower it by hand: tap the category, type the new total and press "Done".',
        ),
        true,
      ),
      disponible: () => hayRojo() && hayFilas(),
    },
  ];

  const noLlega: PasoAyuda[] = [
    {
      id: 'no-llega',
      escena: 'normal',
      radio: 22,
      texto: t(
        'If it does not stretch to everything, you have to choose what goes first. Let us go in order.',
      ),
      objetivo: () => uno(CABECERA),
    },
    pestanas(
      'no-llega-pestanas',
      t(
        'First "Fixed payments": rent, electricity, what you pay no matter what. Then "With target" and, if there is some left, "No target".',
      ),
    ),
    {
      id: 'no-llega-auto',
      escena: 'normal',
      radio: 16,
      texto: t(
        '"Assign by targets" shares out from top to bottom and stops when the money runs out. The line above warns you if it does not stretch to everything.',
      ),
      objetivo: () =>
        todos(
          '[data-testid="auto-asignar-plan"], [data-testid="auto-asignar"]',
        ),
    },
    teclado(
      'no-llega-teclado',
      t(
        'Whatever can wait, lower it by hand: tap the category and type what you can give it this month.',
      ),
      true,
    ),
  ];

  const objetivos: PasoAyuda[] = [
    {
      id: 'objetivos',
      escena: 'normal',
      radio: 16,
      texto: t(
        'It gives each category with a target what it is missing, from top to bottom, as far as the money goes. It only raises amounts, it never lowers them.',
      ),
      objetivo: () => uno('[data-testid="auto-asignar"]'),
    },
    {
      id: 'objetivos-gastado',
      escena: 'normal',
      radio: 12,
      texto: t(
        'With "Include overspending" ticked, it also covers the categories that went over.',
      ),
      objetivo: () => uno('[data-testid="auto-asignar-incluir-gastado"]'),
    },
    {
      id: 'objetivos-plan',
      escena: 'normal',
      radio: 12,
      texto: t(
        'This line tells you first how much it will share out and among how many categories.',
      ),
      objetivo: () => uno('[data-testid="auto-asignar-plan"]'),
    },
    guardar(
      'objetivos-guardar',
      t(
        'Afterwards you see it in the list with the amount from before. If you do not like it, "Discard"; if you do, "Save budget".',
      ),
      true,
    ),
  ];

  const seGuarda: PasoAyuda[] = [
    {
      id: 'guardar-antes',
      escena: 'normal',
      radio: 10,
      texto: t(
        'Not yet. The rows you change show the new amount and, below, the one from before.',
      ),
      objetivo: () => uno(`${FILA}[data-sin-guardar]`),
      etiquetas: () =>
        etiquetasDe(document.querySelector(`${FILA}[data-sin-guardar]`), [
          ['[data-testid="asignar-antes"]', t('Before'), true],
        ]),
    },
    guardar(
      'guardar-barra',
      t(
        'Nothing changes in your Plan until "Save budget". "Discard" leaves everything as it was. And if you save and regret it, the notice has "Undo".',
      ),
    ),
    {
      id: 'guardar-salir',
      escena: 'normal',
      radio: 16,
      final: true,
      texto: t(
        'If you leave or change month with changes halfway, I ask you first: save, discard or stay here.',
      ),
      objetivo: () =>
        todos('[data-testid="asignar-volver"], [data-testid="selector-mes"]'),
    },
  ];

  const masOpciones: PasoAyuda[] = [
    {
      id: 'opciones-reducir',
      escena: 'asignar-mas',
      radio: 12,
      texto: t(
        '"Reduce overfunding": if you gave too much to categories with a target, it gives back what is left over. If it is grey, there is nothing to give back.',
      ),
      objetivo: () => opcion('reduce-overfunding'),
    },
    {
      id: 'opciones-mes-pasado',
      escena: 'asignar-mas',
      radio: 12,
      texto: t(
        '"Assigned last month" repeats what you gave. Careful: it changes all the categories, not only the empty ones.',
      ),
      objetivo: () => opcion('last-month'),
    },
    {
      id: 'opciones-gastado',
      escena: 'asignar-mas',
      radio: 12,
      texto: t(
        '"Spent last month" puts what you really spent. It only touches the categories that spent something.',
      ),
      objetivo: () => opcion('spent-last'),
    },
    {
      id: 'opciones-promedios',
      escena: 'asignar-mas',
      radio: 12,
      final: true,
      texto: t(
        'The two averages use the three previous months and smooth out odd months. Then go over the list: no shortcut knows this month is different.',
      ),
      objetivo: () => [...opcion('avg-assigned'), ...opcion('avg-spent')],
    },
  ];

  const color = (
    id: string,
    estado: string,
    texto: string,
    final = false,
  ): PasoAyuda => ({
    id,
    escena: 'normal',
    radio: 10,
    final,
    texto,
    objetivo: () => filaConEstado(estado),
  });
  const colores: PasoAyuda[] = [
    color(
      'color-rojo',
      'gastado-de-mas',
      t('Red: you have spent more than you gave it.'),
    ),
    color(
      'color-falta',
      'falta',
      t(
        '"Needed", in amber: it has not reached its target yet. Tap the pill and it adds it in one tap.',
      ),
    ),
    color(
      'color-sobra',
      'sobrefinanciada',
      t('"Extra": it has more than its target asks for.'),
    ),
    color(
      'color-financiada',
      'financiada',
      t(
        '"Funded", in green: it already has what its target asks for this month.',
      ),
    ),
    color(
      'color-suficiente',
      'ignorada',
      t(
        '"Enough this month", in grey: you left it alone; it does not ask for anything or count as missing.',
      ),
    ),
    color(
      'color-sin-objetivo',
      'sin-objetivo',
      t(
        '"No target", in grey: it does not ask for anything; give it what you want.',
      ),
      true,
    ),
  ];

  const sobra: PasoAyuda[] = [
    {
      id: 'sobra',
      escena: 'normal',
      radio: 14,
      texto: t(
        'If there is money left in green at the top, it has no job yet. It is not lost: it moves on to next month, but it is better to give it somewhere to go.',
      ),
      objetivo: () =>
        signo() === 'positivo' ? uno('[data-testid="asignar-listo"]') : [],
    },
    {
      ...pestanas(
        'sobra-pestanas',
        t(
          'Check "With target" in case one is short. Then "No target": savings, a cushion for surprises, debts.',
        ),
      ),
      disponible: () => signo() === 'positivo',
    },
    {
      ...teclado(
        'sobra-teclado',
        t(
          'Tap the category, type the amount and press "Done", until the top is at zero.',
        ),
        true,
      ),
      disponible: () => signo() === 'positivo' && hayFilas(),
    },
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    { id: 'rojo', texto: t('It shows red, what do I do?'), pasos: rojo },
    {
      id: 'no-llega',
      texto: t('There is not enough for everything'),
      pasos: noLlega,
    },
    {
      id: 'objetivos',
      texto: t('What does "Assign by targets" do?'),
      pasos: objetivos,
    },
    {
      id: 'se-guarda',
      texto: t('Is what I touch saved already?'),
      pasos: seGuarda,
    },
    {
      id: 'mas-opciones',
      texto: t('Which option in "More options" do I use?'),
      pasos: masOpciones,
    },
    { id: 'colores', texto: t('What do the colors mean?'), pasos: colores },
    {
      id: 'sobra',
      texto: t('What do I do with what is left over?'),
      pasos: sobra,
    },
  ];
}
